<script setup lang="ts">

import type { IMarketplaceCategory, IOfferTile } from "@domain/Marketplace";
import { OfferTile } from "@app/MyMarketplace";
import { OfferHeaderStyle } from "@app/MyMarketplace/Types/OfferHeaderStyle";

type Props = { 
    title?: string, 
    subTitle?: string, 
    totalCount?: number, 
    unfilteredCount?: number,
    groups?: IMarketplaceCategory[] 
};
const model = defineModel<IOfferTile[]>( { required: true } );
const { title, subTitle, totalCount = 0, groups = [] } = defineProps<Props>();

const groupedOffers = ( category: IMarketplaceCategory ) => model.value.filter( o => o.categoryId == category.categoryId );

</script>

<template>

    <div class="flex flex-col gap-2 3xl:gap-3
                bg-gradient-to-b from-gray-900 via-40% via-gray-850 to-gray-900">

        <h1 v-if="title"
            class="px-4 3xl:px-6 flex items-center justify-between tracking-wider text-base 3xl:text-xl">
            <span>{{ title }}</span>
        </h1>

        <!-- LIST IS DIVIDED INTO SUB CATEGORIES -->
        <div v-if="groups.length > 1"
             class="flex flex-col gap-y-4 lg:gap-y-6 3xl:gap-y-10 px-4 3xl:px-6">
            <div v-for="group in groups"
                 :key="group.categoryId">
                <div v-if="groupedOffers( group ).length > 0">
                    <div class="text-lg pb-3 mt-8 first:mt-0 font-semibold">
                        {{ group.name }}
                    </div>
                    <div v-if="groupedOffers( group ).length == 1">
                        <template v-for="(offer, index) in groupedOffers( group )" 
                                  :key="offer.offerId">
                            <OfferTile :offer="offer" :header-style="OfferHeaderStyle.Large" />
                        </template>
                    </div>
                    <div v-else
                         class="grid grid-cols-2 gap-4 3xl:gap-6">
                        <template v-for="(offer, index) in groupedOffers( group )" 
                                  :key="offer.offerId">
                            <OfferTile :offer="offer" :header-style="OfferHeaderStyle.Small" />
                        </template>
                    </div>
                </div>
            </div>
        </div>

        <!-- THERE IS ONLY 1 OFFER, SO GO FULL WIDTH -->
        <div v-else-if="model.length == 1"
             class="px-4 3xl:px-6">
            <template v-for="(offer, index) in model" 
                      :key="offer.offerId">
                <OfferTile :offer="offer" :header-style="OfferHeaderStyle.Large" />
            </template>
        </div>
           
        <!--OTHERWISE LIST THE OFFERS IN TWO COLUMNS -->
        <div v-else
             class="grid grid-cols-2 gap-4 3xl:gap-6 px-4 3xl:px-6">
            <template v-for="(offer, index) in model" 
                      :key="offer.offerId">
                <OfferTile :offer="offer" :header-style="OfferHeaderStyle.Small" />
            </template>
        </div>

    </div>
    
</template>